import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';
import { Spinner } from './Spinner';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'outline';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and disables the button. */
  loading?: boolean;
  /** Leading icon (decorative). */
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

// POC .adm-btn — rounded ~8px, semibold, 1.5px border, subtle hover lift.
const base =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-transparent font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none';

const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-white hover:bg-primary-400 hover:shadow-card active:bg-primary-700',
  secondary:
    'bg-surface-subtle text-surface-dark hover:bg-surface-border dark:bg-surface-dark-subtle dark:text-surface dark:hover:bg-surface-dark-border',
  ghost:
    'bg-transparent text-surface-dark hover:bg-surface-subtle dark:text-surface dark:hover:bg-surface-dark-subtle',
  outline:
    'border-surface-border bg-surface text-surface-dark hover:bg-surface-subtle hover:border-surface-dark/30 dark:border-surface-dark-border dark:bg-surface-dark-muted dark:text-surface dark:hover:bg-surface-dark-subtle',
  danger: 'bg-danger text-white hover:opacity-90 active:opacity-80',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
  icon: 'h-10 w-10 p-0',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      className,
      disabled,
      children,
      type = 'button',
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(base, variants[variant], sizes[size], className)}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...rest}
      >
        {loading ? <Spinner size="sm" /> : leftIcon}
        {children}
        {!loading && rightIcon}
      </button>
    );
  },
);
